import { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { number, oneOfType, string } from 'prop-types'
import { useSelector } from 'react-redux'
import { CHANNELS, getPusherInstance } from '@global/js/pusher'
import { api } from 'api'
import { EventGroup } from './event-group'

const ascEntrySort = (a, b) => {
  return moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf()
}

const reverseEntrySort = (a, b) => {
  return ascEntrySort(b, a)
}

const appendIfMissing = (oldEvents, entry) => {
  const existingEntry = oldEvents.find(e => e.id === entry.id)
  const appended = existingEntry ? oldEvents : [...oldEvents, entry]
  return appended
}

export function DatasetSubmissionEventLog({ datasetId, datasetSubmissionId }) {
  const accountId = useSelector(state => state.Auth.account.id)
  const [events, setEvents] = useState([])
  const Client = getPusherInstance()

  const fetchSubmissionEvents = useCallback(async () => {
    const response = await api.get(
      `/api/v1/account/dataset/${datasetId}/submissions/${datasetSubmissionId}/events`,
    )
    const newEvents = response.data
    setEvents(newEvents)
  }, [datasetSubmissionId, events])

  const subscribeToNewEvents = useCallback(() => {
    if (datasetSubmissionId) {
      const channelDefinition = CHANNELS.ACCOUNT_DATASET_SUBMISSION_CHANGES_SINGLE
      const channel = Client.subscribe(channelDefinition.name(accountId, datasetSubmissionId))
      channel.bind(
        channelDefinition.events.DATASET_SUBMISSION_LOG_EVENT_ADDED,
        payload => {
          setEvents(oldEvents => appendIfMissing(oldEvents, payload.submissionLogEntry))
        },
        'event-log',
      )
    }
  }, [accountId, datasetSubmissionId])

  const subscribeFromNewEvents = useCallback(() => {
    const channelDefinition = CHANNELS.ACCOUNT_DATASET_SUBMISSION_CHANGES_SINGLE
    const channelName = channelDefinition.name(accountId, datasetSubmissionId)
    const channel = Client.channel(channelName)
    if (channel) {
      Client.unsubscribe(channelName)
      channel.unbind(channelDefinition.events.DATASET_SUBMISSION_LOG_EVENT_ADDED, null, 'event-log')
    }
  }, [accountId, datasetSubmissionId])

  useEffect(() => {
    subscribeToNewEvents()
    return subscribeFromNewEvents
  }, [datasetId, datasetSubmissionId])

  useEffect(() => {
    fetchSubmissionEvents()
  }, [datasetId, datasetSubmissionId])

  const groupedEvents = useMemo(
    function () {
      // sort the events in order first
      const copiedEvents = [...events]
      copiedEvents.sort(reverseEntrySort)

      // then group them based on common group type
      // changes of group type create a new visual grouping
      const grouped = copiedEvents.reduce(
        (memo, event) => {
          if (event.groupType !== memo.currentGroup.groupType) {
            memo.currentGroup = {
              id: event.id,
              groupType: event.groupType,
              events: [],
            }
            memo.groups.push(memo.currentGroup)
          }
          memo.currentGroup.events.push(event)
          return memo
        },
        { currentGroup: {}, groups: [] },
      )

      return grouped.groups
    },
    [events],
  )

  return (
    <div>
      {groupedEvents && groupedEvents.map(group => <EventGroup key={group.id} group={group} />)}
    </div>
  )
}

DatasetSubmissionEventLog.propTypes = {
  datasetId: oneOfType([string, number]).isRequired,
  datasetSubmissionId: oneOfType([string, number]).isRequired,
}
